import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Send, Edit, CheckCircle, XCircle } from 'lucide-react';

interface TelegramTopic {
  id: string;
  topic_name: string;
  topic_description: string | null;
  telegram_thread_id: number;
  group_chat_id: number;
  icon_emoji: string | null;
  is_active: boolean;
}

export function TelegramTopicManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingTopic, setEditingTopic] = useState<TelegramTopic | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [threadId, setThreadId] = useState('');
  const [chatId, setChatId] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Fetch topics
  const { data: topics, isLoading } = useQuery({
    queryKey: ['telegram_topics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('telegram_topics')
        .select('id, topic_name, topic_description, telegram_thread_id, group_chat_id, icon_emoji, is_active')
        .order('topic_name');
      
      if (error) throw error;
      return data as TelegramTopic[];
    }
  });

  // Update topic mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<TelegramTopic> }) => {
      const { data, error } = await supabase
        .from('telegram_topics')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telegram_topics'] });
      toast({
        title: 'Success',
        description: 'Topic updated successfully',
      });
      setEditDialogOpen(false);
      setEditingTopic(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  // Test send message mutation
  const testMutation = useMutation({
    mutationFn: async ({ chatId, threadId }: { chatId: number; threadId: number }) => {
      const { data, error } = await supabase.functions.invoke('telegram-send-message', {
        body: {
          chatId,
          messageThreadId: threadId,
          text: '🧪 **Test Message**\n\nThis is a test message sent from the admin panel.\n\nIf you see this, your topic configuration is correct! ✅',
          parseMode: 'Markdown'
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Test Message Sent',
        description: 'Check your Telegram group for the test message',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to Send',
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  const handleEdit = (topic: TelegramTopic) => {
    setEditingTopic(topic);
    setThreadId(topic.telegram_thread_id.toString());
    setChatId(topic.group_chat_id.toString());
    setIsActive(topic.is_active);
    setEditDialogOpen(true);
  };

  const handleSave = () => {
    if (!editingTopic) return;

    const parsedThreadId = parseInt(threadId);
    const parsedChatId = parseInt(chatId);

    if (isNaN(parsedThreadId) || isNaN(parsedChatId)) {
      toast({
        title: 'Validation Error',
        description: 'Thread ID and Chat ID must be valid numbers',
        variant: 'destructive',
      });
      return;
    }

    updateMutation.mutate({
      id: editingTopic.id,
      updates: {
        telegram_thread_id: parsedThreadId,
        group_chat_id: parsedChatId,
        is_active: isActive
      }
    });
  };

  const handleTest = (topic: TelegramTopic) => {
    if (topic.telegram_thread_id === 0 || topic.group_chat_id === 0) {
      toast({
        title: 'Configuration Required',
        description: 'Please configure Thread ID and Chat ID before testing',
        variant: 'destructive',
      });
      return;
    }

    testMutation.mutate({
      chatId: topic.group_chat_id,
      threadId: topic.telegram_thread_id
    });
  };

  const getCategoryColor = (topicName: string) => {
    const name = topicName.toLowerCase();
    if (name.includes('command')) {
      return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
    } else if (name.includes('ai') || name.includes('assistant')) {
      return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
    } else if (name.includes('log') || name.includes('debug')) {
      return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    } else {
      return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    }
  };

  const getCategory = (topicName: string) => {
    const name = topicName.toLowerCase();
    if (name.includes('command')) return 'commands';
    if (name.includes('ai') || name.includes('assistant')) return 'ai';
    if (name.includes('log') || name.includes('debug')) return 'logs';
    return 'notifications';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Telegram Topics Management</CardTitle>
          <CardDescription>
            Configure topics for organized notifications in your Telegram Supergroup
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Setup Instructions */}
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="setup">
              <AccordionTrigger className="text-sm font-semibold">
                📖 Setup Instructions
              </AccordionTrigger>
              <AccordionContent className="space-y-3 text-sm text-muted-foreground">
                <div className="space-y-2">
                  <p className="font-semibold text-foreground">Step 1: Create Telegram Supergroup</p>
                  <ol className="list-decimal list-inside space-y-1 pl-2">
                    <li>Open Telegram and create a new group</li>
                    <li>Add your Telegram bot to the group</li>
                    <li>Go to Group Settings → "Group Type" → Convert to "Supergroup"</li>
                    <li>Enable "Topics" in group settings</li>
                  </ol>
                </div>

                <div className="space-y-2">
                  <p className="font-semibold text-foreground">Step 2: Create Topics</p>
                  <p>Create topics in your Supergroup matching the names shown below:</p>
                  <ul className="list-disc list-inside space-y-1 pl-2">
                    {topics?.map(topic => (
                      <li key={topic.id}>{topic.icon_emoji} {topic.topic_name}</li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-2">
                  <p className="font-semibold text-foreground">Step 3: Get Topic IDs</p>
                  <ol className="list-decimal list-inside space-y-1 pl-2">
                    <li>Send a test message to each topic</li>
                    <li>Check your bot's webhook logs or use Telegram Bot API</li>
                    <li>Note down the <code className="bg-muted px-1 py-0.5 rounded">message_thread_id</code> for each topic</li>
                    <li>Also note the group's <code className="bg-muted px-1 py-0.5 rounded">chat_id</code> (usually negative number)</li>
                  </ol>
                </div>

                <div className="space-y-2">
                  <p className="font-semibold text-foreground">Step 4: Configure Topics</p>
                  <ol className="list-decimal list-inside space-y-1 pl-2">
                    <li>Click the "Edit" button on each topic below</li>
                    <li>Enter the Thread ID and Group Chat ID</li>
                    <li>Enable/disable the topic as needed</li>
                    <li>Click "Save" to update</li>
                    <li>Use "Test Message" to verify configuration</li>
                  </ol>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Topics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {topics?.map((topic) => (
              <Card key={topic.id} className="relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 to-primary/10" />
                
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{topic.icon_emoji}</span>
                      <div>
                        <CardTitle className="text-base">{topic.topic_name}</CardTitle>
                        <CardDescription className="text-xs mt-1">
                          {topic.topic_description}
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className={getCategoryColor(topic.topic_name)}>
                      {getCategory(topic.topic_name)}
                    </Badge>
                    <div className="flex items-center gap-1">
                      {topic.is_active ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="text-xs text-muted-foreground">
                        {topic.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Thread ID:</span>
                      <code className="bg-muted px-1.5 py-0.5 rounded">
                        {topic.telegram_thread_id || 'Not set'}
                      </code>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Chat ID:</span>
                      <code className="bg-muted px-1.5 py-0.5 rounded">
                        {topic.group_chat_id || 'Not set'}
                      </code>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleEdit(topic)}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleTest(topic)}
                      disabled={testMutation.isPending || topic.telegram_thread_id === 0}
                    >
                      {testMutation.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <>
                          <Send className="h-3 w-3 mr-1" />
                          Test
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Edit Topic: {editingTopic?.icon_emoji} {editingTopic?.topic_name}
            </DialogTitle>
            <DialogDescription>
              Update the Telegram configuration for this topic
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="thread-id">Thread ID</Label>
              <Input
                id="thread-id"
                type="number"
                value={threadId}
                onChange={(e) => setThreadId(e.target.value)}
                placeholder="e.g., 2"
              />
              <p className="text-xs text-muted-foreground">
                The message_thread_id from Telegram (integer)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="chat-id">Group Chat ID</Label>
              <Input
                id="chat-id"
                type="number"
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
                placeholder="e.g., -1001234567890"
              />
              <p className="text-xs text-muted-foreground">
                The group's chat_id (usually a negative number)
              </p>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is-active">Active</Label>
              <Switch
                id="is-active"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
